import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { DataCard, styles, SdiSchoolHat, Loader } from '@sdir/sds';
import moment from 'moment';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { CourseEducationResult, ProcessingState } from '@sdir/httpclient/lib/clients/aps/course';
import { InfoPageLayout } from '../../Organisms';
import {
  CoursAndEducationsHeader,
  CoursAndEducationRow,
  TableDefinition,
  CoursAndEducationsHeaderMobile,
  CoursAndEducationRowMobile,
} from '../../../helpers/TableHeaders';
import { SortableDataTable } from '../../Molecules';
import { courseEducationApi } from '../../../httpclient';

export const CourseAndEducationPage: React.FC = () => {
  const intl = useIntl();

  const {
    result: educations,
    errorObject,
    loading,
    callApi,
  } = useApi({
    apiFunction: courseEducationApi.v1CourseeducationUsersowncoursesQueryObjectPost,
    callApiOnInit: true,
    bind: courseEducationApi,
    defaultPayload: {
      filter: ProcessingState.Approved,
      includeExpired: false,
    },
  });
  const [showExpired, setShowExpired] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const toggleExpired = useCallback(() => {
    callApi({
      take: 100,
      skip: 0,
      includeExpired: !showExpired,
      filter: ProcessingState.Approved,
    });
    setShowExpired(!showExpired);
  }, [callApi, showExpired]);

  const filteredEducations = useMemo(() => {
    if (showExpired) return educations;
    return educations?.filter((i) => i.valid);
  }, [showExpired, educations]);

  const table = useMemo<TableDefinition<CoursAndEducationRow> | null>(() => {
    if (!filteredEducations) return null;
    return {
      columns: CoursAndEducationsHeader(intl.formatMessage),
      data: filteredEducations?.map((e) => ({
        courseoreducation: e.courseName || '',
        issued: moment(e.issuedDate).format('DD.MM.YYYY') || '',
        issuedValue: moment(e.issuedDate),
        expiry: moment(e.expiryDate).format('DD.MM.YYYY') || '',
        expiryValue: moment(e.expiryDate),
      })),
      sortableKeys: '*',
    };
  }, [filteredEducations, intl]);

  const mobileTable = useMemo<TableDefinition<CoursAndEducationRowMobile> | null>(() => {
    if (!filteredEducations) return null;
    return {
      columns: CoursAndEducationsHeaderMobile(intl.formatMessage),
      data: filteredEducations?.map((e) => ({
        courseoreducation: e.courseName || '',
        childContent: <RowMobileContent rowData={e} fm={intl.formatMessage} />,
      })),
      sortableKeys: ['courseoreducation'],
      collapsable: true,
      collapsableText: [
        intl.formatMessage({ id: 'general.table.viewdetails' }),
        intl.formatMessage({ id: 'general.table.hidedetails' }),
      ],
    };
  }, [filteredEducations, intl]);

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <InfoPageLayout
      title={intl.formatMessage({ id: 'home.tile.courseAndEducation' })}
      description={intl.formatMessage(
        { id: 'courseandeducation.header.description' },
        { br: () => <br /> }
      )}
      icon={<SdiSchoolHat color={styles.colors.secondary.seaBlue4} size="l" />}
    >
      <Wrapper>
        <DataCard
          title={intl.formatMessage({ id: 'home.tile.courseAndEducation' })}
          onExpandClicked={toggleExpired}
          expandable
          linkText={
            showExpired
              ? intl.formatMessage({ id: 'courseandeducation.table.hideexpired' })
              : intl.formatMessage({ id: 'courseandeducation.table.showexpired' })
          }
        >
          {loading && <Loader />}
          {errorObject && <ErrorMessage error={errorObject} />}
          {table && mobileTable && (
            <TableWrapper data-testid="course-table">
              <SortableDataTable table={isMobile ? mobileTable : table} />
            </TableWrapper>
          )}
        </DataCard>
      </Wrapper>
    </InfoPageLayout>
  );
};

interface RowMobileContentProps {
  rowData: CourseEducationResult;
  fm: any;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({ rowData, fm }) => {
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader2' })}</SubHeader>
        <SubValue>{rowData.organization || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader3' })}</SubHeader>
        <SubValue>{fm({ id: rowData.valid ? 'valid' : 'invalid' })}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader4' })}</SubHeader>
        <SubValue>{moment(rowData.issuedDate).format('DD.MM.YYYY') || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'courseandeducation.table.subheader5' })}</SubHeader>
        <SubValue>{moment(rowData.expiryDate).format('DD.MM.YYYY') || ''}</SubValue>
      </MobileContentItem>
    </>
  );
};

const Wrapper = styled.div`
  button {
    font-weight: bold;
  }
`;

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
